import { useLingui } from "@lingui/react"
import { Group, Modal, Text } from "@mantine/core"
import { speakerDisplayLabel, speakerEntityId, speakerRosterKey } from "../../lib/speaker-vm"
import { SpeakerCard } from "../SpeakerCard"
import type { IProps } from "./interfaces"

/**
 * The single speaker-edit modal, replacing the two hand-rolled Modal+SpeakerCard
 * blocks that lived in MeetingSummaryView and SpeakerPanel. It takes a unified
 * `ISpeakerVM` and forwards the auxiliary data (quotes, profile, segments) to
 * the existing `SpeakerCard`, which owns the rename / merge / discard / profile
 * mutations and the AI-suggestion banner. This is also the affordance the
 * transcript page gains in Wave 4 — any speaker becomes editable by opening it.
 *
 * Title badges are a `ReactNode` prop, not a SpeakerChip import: the badge
 * primitive is owned by a parallel wave, so keeping the seam a plain node lets
 * the host compose the badges and hands this modal no coupling to it.
 */

export function SpeakerEditModal({
  meetingId,
  opened,
  speaker,
  allSpeakers,
  quotes,
  profile,
  autoColor,
  autoTitle,
  speakerSegments,
  audioUrl,
  titleBadges,
  onClose,
  onEdited,
}: IProps): React.JSX.Element {
  const { i18n } = useLingui()
  // Remount the card when the edited speaker changes identity — prefer the stable
  // entity id, falling back to the roster key on a legacy (id-less) meeting.
  const identity = speaker ? (speakerEntityId(speaker) ?? speakerRosterKey(speaker)) : ""

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="md"
      title={
        speaker ? (
          <Group gap="sm">
            <Text fw={600}>{speakerDisplayLabel(speaker, i18n)}</Text>
            {titleBadges}
          </Group>
        ) : null
      }
    >
      {speaker && (
        <SpeakerCard
          key={identity}
          meetingId={meetingId}
          speaker={speaker}
          allSpeakers={allSpeakers}
          quotes={quotes ?? []}
          profile={profile}
          autoColor={autoColor ?? speaker.resolvedColor}
          autoTitle={autoTitle ?? speaker.title ?? undefined}
          speakerSegments={speakerSegments}
          audioUrl={audioUrl}
          defaultOpen
          onRenamed={() => {
            onClose()
            onEdited?.()
          }}
        />
      )}
    </Modal>
  )
}
